import Map, { Layer, Marker, Source } from "react-map-gl";
import PlaceIcon from "@mui/icons-material/Place";
import "mapbox-gl/dist/mapbox-gl.css";
import "@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css";
import VideocamIcon from "@mui/icons-material/Videocam";
import * as turf from "@turf/turf";
import planConfig from "../../../config/planConfig";
const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;
const MarkerComponent = ({ marker, type }) => {
  const markerId = marker.properties.id;
  const arrowPos = marker?.properties?.arrowProperties || {
    angle: 270,
    x: 0,
    y: -30,
  };

  return (
    <Marker
      key={markerId}
      longitude={marker.geometry.coordinates[0]}
      latitude={marker.geometry.coordinates[1]}
      anchor="bottom"
    >
      <div
        style={{
          position: "relative",
          width: "50px",
          height: "50px",
          textAlign: "center",
          "--arrow-x": `calc(50% + ${arrowPos.x}px)`,
          "--arrow-y": `calc(50% + ${arrowPos.y}px)`,
          "--arrow-angle": `${arrowPos.angle || 270}deg`,
        }}
      >
        {/* Draggable Navigation Icon */}
        {type !== planConfig?.ground_control?.ground_control_points_plan && (
          <div
            style={{
              position: "absolute",
              left: "var(--arrow-x)",
              top: "var(--arrow-y)",
              transform: "translate(-50%, -50%) rotate(var(--arrow-angle))",
              width: "25px",
              height: "25px",
              color: "rgb(52, 167, 255)",
              zIndex: 100,
            }}
          >
            <VideocamIcon />
          </div>
        )}

        {/* Marker Icon (centered) */}
        <PlaceIcon
          sx={{
            position: "absolute",
            left: "50%",
            top: "50%",
            transform: "translate(-50%, -50%)",
            width: 30,
            height: 30,
            color: "rgb(255, 134, 52)",
          }}
        />
      </div>
    </Marker>
  );
};

const calculateCenter = (features) => {
  if (!features || features.length === 0) return [0, 0];
  const geojson = {
    type: "FeatureCollection",
    features,
  };

  // Handle mixed types properly
  if (features.some((f) => f?.geometry?.type === "Point")) {
    return turf.center(geojson).geometry.coordinates;
  }
  if (features.some((f) => f?.geometry?.type === "LineString")) {
    const lineStrings = features.filter(
      (f) => f?.geometry.type === "LineString"
    );
    const allCoordinates = lineStrings.flatMap((f) => f.geometry.coordinates);
    const line = turf.lineString(allCoordinates);
    return turf.midpoint(
      line.geometry.coordinates[0],
      line.geometry.coordinates[line.geometry.coordinates.length - 1]
    ).geometry.coordinates;
  }
  return turf.centroid(geojson).geometry.coordinates;
};

const PhotosMap = ({ features = [], zoomLevel, type }) => {
  if (!features.length) return null;

  const [longitude, latitude] = calculateCenter(features);

  return (
    <Map
      style={{ height: 290 }}
      initialViewState={{
        latitude,
        longitude,
        zoom: zoomLevel - 1 || 16,
      }}
      mapStyle="mapbox://styles/mapbox/satellite-streets-v12"
      mapboxAccessToken={MAPBOX_TOKEN}
      dragPan={true}
      scrollZoom={true}
      attributionControl={false}
    >
      {features.map((feature, idx) => {
        const { id, geometry } = feature;

        if (geometry.type === "Point") {
          return <MarkerComponent key={idx} marker={feature} type={type} />;
        }
        if (geometry.type === "LineString") {
          return (
            <Source key={idx} id={`line-${id}`} type="geojson" data={feature}>
              <Layer
                id={`line-layer-${id}`}
                type="line"
                paint={{
                  "line-color": "rgb(255, 134, 52)",
                  "line-width": 5,
                }}
              />
            </Source>
          );
        }
        if (geometry.type === "Polygon") {
          return (
            <Source key={idx} id={`polygon-${id}`} type="geojson" data={feature}>
              <Layer
                id={`polygon-layer-${id}`}
                type="fill"
                paint={{
                  "fill-color": "rgba(216, 126, 41, 0.5)",
                  "fill-outline-color": "rgb(255, 134, 52)",
                  "fill-opacity": 0.6,
                }}
              />
            </Source>
          );
        }
        return null;
      })}
    </Map>
  );
};

export default PhotosMap;
